import analyticsService, {
  AppEvent
} from 'infrastructure/serviceHandlers/Analytics/Analytics';
import { EVENT_PROPERTIES } from 'infrastructure/serviceHandlers/Analytics/eventMapper';
import {
  useFlag,
  useFlagsStatus
} from 'infrastructure/serviceHandlers/Analytics/useFlag';
import {
  type ComponentType,
  lazy,
  type LazyExoticComponent,
  Suspense,
  useEffect,
  useState
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  decideVariant,
  parseOverride,
  pickVariant,
  readLock,
  writeLock
} from './assignment';
import type { Experiment, ScreenLoader } from './registry';

// How long a screen waits for the identified user's flag values, counted from
// the moment eligibility is known. This wait is added in front of the screen
// in both arms — the screen's own data fetches only start once it mounts — so
// it's kept short, and the variant chunks and the eligibility requests are
// started alongside it rather than after.
const FLAGS_TIMEOUT_MS = 1500;

const alwaysEligible = () => true;

// One lazy component per loader, so a remount doesn't load the screen again.
const screens = new Map<ScreenLoader, LazyExoticComponent<ComponentType>>();
const screenFor = (load: ScreenLoader) => {
  let screen = screens.get(load);
  if (!screen) {
    screen = lazy(load);
    screens.set(load, screen);
  }
  return screen;
};

/**
 * The variant this user sees, or undefined while it is still being decided.
 *
 * Decided once per mount and never changed after, so a later eligibility or
 * flag change can't unmount the screen. In order:
 *  1. a QA override in the URL — renders that variant, no analytics;
 *  2. not eligible — control, no lock, no exposure;
 *  3. the variant already locked this session;
 *  4. the flag, once the identified user's values have landed — locked,
 *     reported as an exposure and stored on the profile, so every other event
 *     can be split by variant;
 *  5. timed out waiting for values — control for this visit, but no lock and
 *     no exposure: slow loads stay out of the experiment instead of piling up
 *     in control.
 */
function useExperimentVariant(experiment: Experiment): string | undefined {
  const { key, flag, variants } = experiment;
  const useEligible = experiment.useEligible ?? alwaysEligible;
  const eligible = useEligible();
  // Timed from eligibility, which needs the session — see useFlagsStatus.
  const flagsStatus = useFlagsStatus(FLAGS_TIMEOUT_MS, eligible === true);
  const flagValue = useFlag(flag);
  const { search } = useLocation();

  const [variant, setVariant] = useState<string | undefined>(() => {
    const override = parseOverride(search, key);
    if (!override) return undefined;
    writeLock(key, override);
    return pickVariant(override, variants);
  });

  useEffect(() => {
    if (variant) return;
    const decision = decideVariant({
      eligible,
      locked: eligible ? readLock(key) : null,
      flagsStatus,
      flagValue,
      variants
    });
    if (!decision) return;
    if (decision.fresh) {
      writeLock(key, decision.variant);
      analyticsService.trackEvent(AppEvent.EXPERIMENT_VIEWED, {
        [EVENT_PROPERTIES.EXPERIMENT]: key,
        [EVENT_PROPERTIES.VARIANT]: decision.variant
      });
      analyticsService.setUserProperties({ [`exp_${key}`]: decision.variant });
    }
    setVariant(decision.variant);
  }, [variant, eligible, flagsStatus, flagValue, key, variants]);

  return variant;
}

/** Renders the experiment's screen for this user's variant. */
function ExperimentRoute({ experiment }: { experiment: Experiment }) {
  const variant = useExperimentVariant(experiment);

  // Download every variant's chunk while the variant is being decided, so the
  // chosen screen is ready the moment it is.
  useEffect(() => {
    Object.values(experiment.variants).forEach((load) => {
      load().catch(() => {
        // Retried by lazy() when the screen renders; nothing to do here.
      });
    });
  }, [experiment.variants]);

  if (!variant) return experiment.loader;

  const Screen = screenFor(experiment.variants[variant]);
  return (
    <Suspense fallback={experiment.loader}>
      <Screen />
    </Suspense>
  );
}

export default ExperimentRoute;
